
import {Component, Vue, Watch} from "vue-property-decorator";
import {MdbsCore} from "@/api/tsclient";
import {currentUserModule, dataMigrationModule} from "@/store";
import {startDownload} from "@/auxillary/export.helper";
import {DateTime} from "luxon";
import Rights = MdbsCore.Rights;
import adminGuides from "@/assets/documents/adminGuides";

@Component
export default class DataMigrationView extends Vue {
  tab = 0;
  historyTab = 0;
  runningAll = false;
  runningId: string | null = null;

  /** Migration chosen in the per-migration sub-tab; its log is only fetched on request. */
  selectedMigrationId: string | null = null;

  confirmDialog = false;
  pendingAll = false;
  pendingMigration: MdbsCore.DataMigrationStatusDto | null = null;

  async mounted() {
    await this.refresh();
  }

  @Watch("tab")
  onTabChanged(tab: number) {
    // Load the history the first time the "Verlauf" tab is opened.
    if (tab === 1 && this.canSeeHistory && this.history.length === 0) {
      dataMigrationModule.loadHistory();
    }
  }

  @Watch("selectedMigrationId")
  onSelectedMigrationChanged() {
    // Picking another migration clears the table until it is loaded again, so the heading and the
    // rows can never disagree about which migration is shown.
    if (this.selectedMigrationId !== this.loadedMigrationId) {
      dataMigrationModule.resetMigrationHistory();
    }
  }

  get loading() {
    return dataMigrationModule.isLoading;
  }

  get running() {
    return this.runningAll || this.runningId !== null;
  }

  get sortedStatuses() {
    return [...dataMigrationModule.statuses].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  get report() {
    return dataMigrationModule.report;
  }

  get hasPending() {
    return dataMigrationModule.statuses.some(m => !m.completed);
  }

  get reportResults() {
    return this.report?.results ?? [];
  }

  get history() {
    return dataMigrationModule.history;
  }

  get migrationHistory() {
    return dataMigrationModule.migrationHistory;
  }

  /** Migration the currently displayed per-migration log belongs to. */
  get loadedMigrationId() {
    return dataMigrationModule.migrationHistoryId;
  }

  get migrationOptions() {
    return this.sortedStatuses
        .filter(m => !!m.migrationId)
        .map(m => ({value: m.migrationId as string, text: m.migrationId as string}));
  }

  get currentHistoryHasEntries() {
    return this.historyTab === 0
        ? this.history.length > 0
        : this.migrationHistory.length > 0;
  }

  get canRefreshCurrentHistory() {
    return this.historyTab === 0 || !!this.loadedMigrationId;
  }

  get canSee() {
    return currentUserModule.hasRight(Rights.DataMigrationGetAllCmd);
  }

  get canSeeHistory() {
    return currentUserModule.hasRight(Rights.AuditLogGetEntriesCmd);
  }

  get canRun() {
    return currentUserModule.hasRight(Rights.DataMigrationRunAllCmd)
        || currentUserModule.hasRight(Rights.DataMigrationRunCmd);
  }

  outcomeLabel(result: MdbsCore.DataMigrationResultDto): string {
    const map: Record<string, string> = {
      Executed: "Ausgeführt",
      ReExecuted: "Erneut ausgeführt",
      Skipped: "Übersprungen",
      AlreadyCompleted: "Bereits ausgeführt",
      Failed: "Fehlgeschlagen",
    };
    if (result.outcome && map[result.outcome]) return map[result.outcome];
    if (result.outcome) return result.outcome;
    return result.error ? "Fehlgeschlagen" : "OK";
  }

  // Erneute Ausführung ist nur erlaubt, wenn die Migration das explizit zulässt
  isRerunBlocked(m: MdbsCore.DataMigrationStatusDto): boolean {
    return !!m.completed && !m.rerunAllowed;
  }

  formatDate(date?: Date): string {
    if (!date || date.getFullYear() <= 1) return "—";
    return DateTime.fromJSDate(date).toFormat("dd.MM.yyyy HH:mm");
  }

  formatDateTime(date?: Date): string {
    if (!date) return "—";
    return DateTime.fromJSDate(date).toFormat("dd.MM.yyyy HH:mm:ss");
  }

  isSuccess(entry: MdbsCore.AuditLogEntryDto): boolean {
    const tags: any = entry.tags;
    // Treat entries without an explicit failure flag as successful.
    return !tags || tags.Success !== false;
  }

  param(entry: MdbsCore.AuditLogEntryDto, key: string): any {
    const params: any = entry?.parameters;
    return params ? params[key] : undefined;
  }

  // Audit parameters carry StartedAt / FinishedAt as ISO date strings.
  formatParamDateTime(value: any): string {
    if (!value) return "—";
    const dt = DateTime.fromISO(String(value));
    return dt.isValid ? dt.toFormat("dd.MM.yyyy HH:mm:ss") : String(value);
  }

  async refresh() {
    if (!this.canSee) return;
    await dataMigrationModule.loadAll();
  }

  async refreshCurrentTab() {
    if (this.tab === 1 && this.canSeeHistory) {
      await this.refreshCurrentHistory();
    } else {
      await this.refresh();
    }
  }

  /** Reloads whichever history sub-tab is open; the per-migration one only if something is loaded. */
  async refreshCurrentHistory() {
    if (this.historyTab === 0) {
      await dataMigrationModule.loadHistory();
      return;
    }
    if (this.loadedMigrationId) {
      await dataMigrationModule.loadMigrationHistory(this.loadedMigrationId);
    }
  }

  async loadMigrationHistory() {
    if (!this.selectedMigrationId) return;
    await dataMigrationModule.loadMigrationHistory(this.selectedMigrationId);
  }

  async downloadCurrentHistory() {
    const response = this.historyTab === 0
        ? await dataMigrationModule.exportHistory()
        : await dataMigrationModule.exportMigrationHistory(this.loadedMigrationId ?? "");

    if (!response) return;

    const fallback = this.historyTab === 0
        ? "data-migration-audit-log.csv"
        : `${this.loadedMigrationId ?? "migration"}-audit-log.csv`;
    startDownload(response.data, response.fileName ?? fallback);
  }

  /** Colours the per-field outcome; only unmapped values are a problem worth highlighting. */
  outcomeColor(entry: MdbsCore.AuditLogEntryDto): string {
    const outcome = this.param(entry, "Outcome");
    if (outcome === "Unmapped") return "warning";
    if (!this.isSuccess(entry)) return "error";
    return "success";
  }

  confirmRunAll() {
    if (this.running || this.loading) return;
    this.pendingAll = true;
    this.pendingMigration = null;
    this.confirmDialog = true;
  }

  confirmRunSingle(m: MdbsCore.DataMigrationStatusDto) {
    if (this.running || this.loading || this.isRerunBlocked(m)) return;
    this.pendingAll = false;
    this.pendingMigration = m;
    this.confirmDialog = true;
  }

  cancelConfirm() {
    this.confirmDialog = false;
    this.pendingAll = false;
    this.pendingMigration = null;
  }

  async proceedConfirm() {
    if (this.running) return;

    const runAll = this.pendingAll;
    const migration = this.pendingMigration;
    this.confirmDialog = false;

    if (runAll) {
      this.runningAll = true;
      try {
        await dataMigrationModule.runAll();
      } finally {
        this.runningAll = false;
      }
    } else if (migration?.migrationId) {
      this.runningId = migration.migrationId;
      try {
        // Force nur, wenn die bereits ausgeführte Migration erneut laufen soll
        await dataMigrationModule.run({migrationId: migration.migrationId, force: !!migration.completed});
      } finally {
        this.runningId = null;
      }
    }

    // Ein Lauf erzeugt neue Audit-Log-Einträge -> Verlauf aktualisieren.
    if (this.canSeeHistory) {
      await dataMigrationModule.loadHistory();

      // Wird gerade das Log der eben gelaufenen Migration angezeigt, ist es jetzt veraltet.
      if (this.loadedMigrationId && (runAll || this.loadedMigrationId === migration?.migrationId)) {
        await dataMigrationModule.loadMigrationHistory(this.loadedMigrationId);
      }
    }

    this.pendingAll = false;
    this.pendingMigration = null;
  }

  get guides() {
    return adminGuides;
  }

  openGuide(guide: { link: string }) {
    window.open(guide.link, "_blank");
  }

}
